import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import { TablePage } from "@outpave/ds";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import PaymentLinksTable, {
  PaymentLinkFilterCriteriaProps,
} from "components/admin/paymentLinks/PaymentLinksTable";
import PaymentLinkDrawerRouter from "components/admin/paymentLinks/PaymentLinkDrawerRouter";
import { PaymentLinkDrawerType, PaymentLinkStatus } from "enums/PaymentLink";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import useGetParamFromUrl from "hooks/useGetParamFromUrl";
import Role from "enums/Role";

const PaymentLinks: React.FC = () => {
  const { t } = useTranslation();
  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });

  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);
  const [refreshIndex, setRefreshIndex] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState<PaymentLinkDrawerType | null>(
    null,
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [filterCriteria, setFilterCriteria] =
    useState<PaymentLinkFilterCriteriaProps>({
      status: [PaymentLinkStatus.ACTIVE],
      searchTerm: "",
    });

  const tabConfigurations = [
    { label: t("Active"), status: [PaymentLinkStatus.ACTIVE] },
    { label: t("Inactive"), status: [PaymentLinkStatus.INACTIVE] },
    { label: t("Show All"), status: Object.values(PaymentLinkStatus) },
  ];

  useEffect(() => {
    setFilterCriteria({
      status: tabConfigurations[activeTab].status,
      searchTerm,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchTerm]);

  const handleRefresh = () => setRefreshIndex((previous) => previous + 1);

  const openDrawer = (
    type: PaymentLinkDrawerType,
    paymentLinkId: string | null = null,
  ) => {
    setDrawerType(type);
    setSelectedId(paymentLinkId);
    setDrawerOpen(true);
  };

  const handleViewDetails = (paymentLinkId: string) =>
    openDrawer(PaymentLinkDrawerType.Details, paymentLinkId);

  const handleEdit = (paymentLinkId: string) =>
    openDrawer(PaymentLinkDrawerType.Edit, paymentLinkId);

  const resetParam = useGetParamFromUrl("id", handleViewDetails);

  const handleCloseDrawer = (
    refresh: boolean = false,
    openDetailsWithId?: string,
  ) => {
    if (refresh) handleRefresh();
    resetParam();

    if (openDetailsWithId) {
      openDrawer(PaymentLinkDrawerType.Details, openDetailsWithId);
      return;
    }

    setSelectedId(null);
    setDrawerOpen(false);
    setDrawerType(null);
  };

  return (
    <TablePage
      title={t("Payment Links")}
      primaryAction={
        isAuditor ? undefined : (
          <Button onClick={() => openDrawer(PaymentLinkDrawerType.Create)}>
            {t("New Payment Link")}
          </Button>
        )
      }
      tabs={{
        items: tabConfigurations.map((tab, index) => ({
          id: String(index),
          label: t(tab.label),
        })),
        activeId: String(activeTab),
        onChange: (id) => setActiveTab(Number(id)),
      }}
      search={search}
    >
      <>
        <PaymentLinksTable
          filterCriteria={filterCriteria}
          refreshIndex={refreshIndex}
          readOnly={isAuditor}
          onViewDetails={handleViewDetails}
          onEdit={handleEdit}
          onRefresh={handleRefresh}
        />

        {drawerType && (
          <PaymentLinkDrawerRouter
            open={drawerOpen}
            drawerType={drawerType}
            paymentLinkId={selectedId || undefined}
            readOnly={isAuditor}
            onClose={handleCloseDrawer}
            onEdit={handleEdit}
          />
        )}
      </>
    </TablePage>
  );
};

export default PaymentLinks;
